import styled from 'styled-components';
import { Clef } from '../../../../../common/src/learn_rules.ts';
import { ButtonColumn, TextButton, ToggleButton } from '../learn_buttons.tsx';
import { HearingYouMeter } from '../learn_meter.tsx';
import { attemptPips, ProgressPipsRow, ScreenBody, ScreenFoot, Slack } from '../learn_screen_parts.tsx';
import { NoteHintBlock, noteHintColors, NoteStaffRow, SkipNoteLink } from './note_screen_parts.tsx';
import {
  dotFor,
  holdProgress,
  isGuideSounding,
  isListening,
  octaveNoteText,
  PitchHint,
  pitchHint,
  pitchHintText,
  pitchNoteIndex,
  PitchSessionState,
} from './pitch_match_session.ts';
import { PitchStaff } from './pitch_staff.tsx';

// One note of a Pitch Match session (issue #3518), from the "Pitch Match, staff version" artboards:
// the "Note 4 of 10" row with its pips, the staff, the hint block (the hint, the octave note, the
// hold label and the hold meter), "Skip this note", the live "Hearing you" meter and "Play again".
// Presentational: everything is read from the state through the session's selectors, so a story can
// render any moment of a note from a state it builds by hand.
//
// The meter is inline under the skip link, the way the Range Finder's direction screen carries it
// (issue #3518, Michael's feedback): a singer who wants to know whether the app can hear them can
// see it without opening anything, so the mic button and the sheet it opened are gone and "Play
// again" has the button row to itself.
//
// "Play again" says two things with one word. While the guide is still ringing - or is still on its
// way, the soundfont loading under "Listen…" - it is outlined: the note is in the air, so hearing
// it again is there if it is wanted but is not what to do next. Once the piano has faded it goes
// solid, because by then it is the only way to hear the note again. It is one button throughout
// (ToggleButton), not two swapped for each other, so the element under the singer's finger
// survives the change.
//
// "Save last 30 s" is a developer's link, not the singer's (issue #3587): on a dev server it saves
// the audio behind a flicker after the fact, with the actions the screen was drawn from, so the
// moment can be replayed on the developer's machine. The page offers it only there, and only once
// there is audio to save, so it is absent in production and in every story.

export const pitchNoteScreenId = 'pitch-note';
export const pitchReplayButtonId = 'pitch-replay-button';
export const pitchSkipButtonId = 'pitch-skip-button';
export const pitchSaveCaptureButtonId = 'pitch-save-capture-button';

export const pitchNoteRowTestId = 'pitch-note-row';
export const pitchPipTestId = 'pitch-pip';

export const replayText = 'Play again';
export const saveCaptureText = 'Save last 30 s';

// Where a save of the last 30 s has got to; nothing is shown once the page clears it
export type PitchCaptureStatus = 'saving' | 'saved' | 'failed';

export const captureStatusText: Record<PitchCaptureStatus, string> = {
  saving: 'Saving…',
  saved: 'Saved to your Mac',
  failed: 'Save failed',
};

// The hint's colour says as much as its words (note_screen_parts.tsx)
const hintColors: Record<PitchHint, string> = noteHintColors;

export const PitchNoteScreen = ({
  state,
  clef,
  onReplay,
  onSkip,
  onSaveCapture,
  captureStatus,
}: {
  state: PitchSessionState;
  clef: Clef; // the singer's, from clefForVoicePart
  onReplay: () => void;
  onSkip: () => void;
  onSaveCapture?: () => void; // dev servers only, once there is audio to save; no row without it
  captureStatus?: PitchCaptureStatus;
}) => {
  const note = state.note;
  const noteIndex = pitchNoteIndex(state);
  const hint = pitchHint(state);
  const listening = isListening(state);
  const guideSounding = isGuideSounding(state);
  const locked = note.outcome?.kind === 'locked';
  // The meter fills green only on a reading that counts, exactly as the dot is coloured: a weak
  // reading on the note draws yellow, because the meter under it cannot move (issue #3518)
  const counting = locked || (note.reading?.inTolerance === true && !note.reading.weak);
  const { heldMs, holdMs } = holdProgress(state);
  const octaveNote = octaveNoteText(state);
  return (
    <ScreenBody
      id={pitchNoteScreenId}
      data-note-index={noteIndex}
      data-target={note.targetMidi}
      data-hint={hint}
      data-listening={listening ? 'true' : 'false'}
      data-guide-sounding={guideSounding ? 'true' : 'false'}
      data-outcome={note.outcome?.kind ?? 'none'}
    >
      <ProgressPipsRow
        label={`Note ${noteIndex + 1} of ${state.targets.length}`}
        pips={attemptPips(noteIndex, state.targets.length)}
        rowTestId={pitchNoteRowTestId}
        pipTestId={pitchPipTestId}
      />
      <NoteStaffRow>
        <PitchStaff clef={clef} targetMidi={note.targetMidi} dot={dotFor(state)} locked={locked} />
      </NoteStaffRow>
      <NoteHintBlock
        hint={hint}
        text={pitchHintText(hint)}
        color={hintColors[hint]}
        octaveText={octaveNote}
        heldMs={heldMs}
        holdMs={holdMs}
        counting={counting}
      />
      <SkipNoteLink id={pitchSkipButtonId} disabled={note.outcome !== undefined} onClick={onSkip} />
      {onSaveCapture && (
        <CaptureRow>
          <CaptureButton
            id={pitchSaveCaptureButtonId}
            data-testid={pitchSaveCaptureButtonId}
            disabled={captureStatus === 'saving'}
            onClick={onSaveCapture}
          >
            {saveCaptureText}
          </CaptureButton>
          {captureStatus && <CaptureStatusText>{captureStatusText[captureStatus]}</CaptureStatusText>}
        </CaptureRow>
      )}
      <Slack />
      <HearingYouMeter level={note.level} />
      <ScreenFoot>
        <ButtonColumn>
          <ToggleButton
            id={pitchReplayButtonId}
            data-testid={pitchReplayButtonId}
            $solid={!guideSounding}
            disabled={note.outcome !== undefined}
            onClick={onReplay}
          >
            {replayText}
          </ToggleButton>
        </ButtonColumn>
      </ScreenFoot>
    </ScreenBody>
  );
};

const CaptureRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

// A developer's link, so it is set as small and as quiet as the octave note rather than as a
// second Skip
const CaptureButton = styled(TextButton)`
  font-family: inherit;
  font-size: 13px;
  color: var(--color-text-darker);
`;

const CaptureStatusText = styled.span`
  font-size: 13px;
  line-height: 18px;
  color: var(--color-text-darker);
`;
